import { PageLayout } from './utilities/enums';
import { NextPagePropertiesModel } from './models/nextpage-properties';
import { UI } from './nextpage';

export class NextPageLayoutService {
    private currentLayout: PageLayout = PageLayout.Layout39;

    constructor(private NP: UI, properties: NextPagePropertiesModel) {
        this.NP.components.layoutMorphs = (): void => {
            this.checkLayoutHeight(properties);
            this.checkNavContainerHeight(properties);
            this.checkMegaMenuSizeAndPlacement(properties);
        }
    }
    

    public getCurrentLayout = (): PageLayout => {
        return this.currentLayout;
    }

    public setLayout(newLayout: PageLayout): Array<boolean> {
        this.setCurrentLayout(newLayout);
        switch (newLayout) {
            case PageLayout.Layout12:
                return [false, false];
            case PageLayout.Layout363:
                return [true, true];
            default:
                return [true, false];
        }
    }

    public getLayoutClassName(pageLayout?: PageLayout): string {
        const thisLayout = pageLayout || this.currentLayout;
        switch (thisLayout) {
            case PageLayout.Layout12:
                return 'layout-12';
            case PageLayout.Layout363:
                return 'layout-363';
            case PageLayout.Layout39:
                return 'layout-39';
            case PageLayout.Layout93:
                return 'layout-93';
            case PageLayout.Layout66:
                return 'layout-66';
        }
    }

    public layoutMorphs = (properties: NextPagePropertiesModel): void => {
        this.checkLayoutHeight(properties);
        this.checkNavContainerHeight(properties);
        this.checkMegaMenuSizeAndPlacement(properties);
    }

    protected setCurrentLayout = (newLayout: PageLayout): void => {
        document.body.classList.remove('layout-12', 'layout-39', 'layout-93', 'layout-363', 'layout-66');
        document.body.classList.add(this.getLayoutClassName(newLayout));
        this.currentLayout = newLayout;
    }

    protected checkLayoutHeight(properties: NextPagePropertiesModel): void {
        let containerHeight = '';
        const layoutHeaders = document.getElementsByTagName('header');
        if (layoutHeaders.length === 0) {
            return;
        }
        const layoutHeader = layoutHeaders[0] as HTMLElement;
        const headerHeight = layoutHeader.clientHeight;

        // If we're on a tablet layout ensure that the height of the content columns is equal.
        // This ensures that they flex and flow correctly when the 363 layout combines columns.
        if (properties.isTablet && document.body.classList.contains('layout-363')) {
            const col1 = document.getElementById('column1');
            const col2 = document.getElementById('column2');
            const col3 = document.getElementById('column3');
            if (col1 === undefined || col2 === undefined || col3 === undefined) {
                return;
            }
            const leftHeight = col1.clientHeight + col3.clientHeight;
            const rightHeight = col2.clientHeight;
            containerHeight = (leftHeight > rightHeight ? leftHeight : rightHeight) + 30 + 'px';
        }
        // Update the height, padding, and position of elements depending on whether you're using
        // sticky navigation.
        const mains = document.querySelectorAll('main.container');
        if (mains.length > 1) {
            console.log('You must have exactly one <main class="container"> element that contains all columns.');
            return;
        }
        const main = mains[0] as HTMLElement;
        const siteSpecNav = document.getElementById('site-specific-nav') as HTMLElement;
        if (document.body.classList.contains('sticky-nav')) {
            main.style.height = containerHeight;
            main.style.paddingTop = (properties.isMobile || properties.isTablet)
                ? (headerHeight + 15) + 'px'
                : (headerHeight + 60) + 'px';
            if (siteSpecNav === undefined) {
                return;
            }
            siteSpecNav.style.top = headerHeight + 'px';
        } else {
            main.style.height = containerHeight;
            main.style.removeProperty('paddingTop');
            if (siteSpecNav === undefined) {
                return;
            }
            siteSpecNav.style.removeProperty('top');
        }
    }

    public checkNavContainerHeight(properties: NextPagePropertiesModel): void {
        const navContainer = document.querySelector("#nav-container") as HTMLDivElement;
        const mainNav = navContainer.querySelector(".mainnav");
        const activeSubNavs = navContainer.querySelectorAll(".subnav.active");
        const activeSubNav = activeSubNavs.length > 0 ? activeSubNavs[activeSubNavs.length - 1] : null;
        const activeNavHeight = (activeSubNav === null ? (mainNav === null ? 0 : mainNav.clientHeight) : activeSubNav.clientHeight) + "px";
        // Set the height of the navigation container to the height of the active navigation element.
        // Also toggle whether a sub-navigation is active with the .subnav-active class.
        navContainer.classList.toggle("subnav-active", activeSubNav !== null);
        navContainer.style.height = activeNavHeight;
        
        const topMenu = document.querySelector("#top-menu") as HTMLElement;
        const header = document.querySelector("header") as HTMLElement;
        if (topMenu === null || header === null) {
            return;
        }
        const headerHeight = header.clientHeight;
        const menuOpen = topMenu.classList.contains("aria-expanded");
        // If the layout is not mobile set the top menu position at the bottom of the header.
        // Also set the max-height to either 0 or none depending on whether the menu is open.
        if (!properties.isMobile) {
            topMenu.style.top = headerHeight + "px";
            return;
        }
        // If the menu is not open set the max-height to 0.
        if (!menuOpen) {
            topMenu.style.top = headerHeight + "px";
            return;
        }
        // Otherwise if the menu is open set the max-height to the window height minus header height.
        topMenu.style.top = headerHeight + "px";
    }

    public checkMegaMenuSizeAndPlacement(properties: NextPagePropertiesModel): void {
        if (properties.isMobile || properties.isTablet) {
            return;
        }
        const siteSpecificNav = document.querySelector("#site-specific-nav") as HTMLElement;
        if (siteSpecificNav === null) {
            return;
        }
        const megaMenus = siteSpecificNav.querySelectorAll(".mega-menu");
        if (megaMenus.length === 0) {
            return;
        }
    
        megaMenus.forEach((menu: HTMLElement) => {
            const actualOffset = menu.parentElement.offsetLeft - siteSpecificNav.offsetLeft;
            menu.style.left = "-" + actualOffset + "px";
            menu.style.width = siteSpecificNav.clientWidth + 'px';
        });
    }
}
